/**
 * HTTPリクエストを扱うモジュール
 */
import { ErrorResponse } from 'common/modules/app/errors';

/** 再試行回数 */
const MAX_RETRIES = 4;

export interface ResponseInterface<
  T extends { [key: string]: any } | undefined
> {
  statusCode?: number;
  body?: T;
  error?: { [key: string]: any };
}

export interface RequestHeader {
  'User-Agent'?: string;
  'Content-Type'?: string;
  'Content-Length'?: number;
  'sas-client-request-id'?: string;
  Cookie?: string;
  Authorization?: string;
}

/**
 * 16桁のRequestIDを作成
 */
export const createRequestId = () => {
  return Array.from('RRRRRRRR4RRRRRRR').reduce((accumulator, current) => {
    switch (current) {
      case 'R':
        // 0～9、a～f
        return accumulator + Math.floor(Math.random() * 16).toString(16);
      default:
        // 4はそのまま
        return accumulator + current;
    }
  }, '');
};

const createResponse = async <T extends Record<string, unknown> | undefined>(
  config: RequestInit,
  response: Response,
  ignoreUnauthorizedHandling?: boolean
): Promise<ResponseInterface<T>> => {
  if (response.status === 204) {
    return { statusCode: response.status };
  }
  try {
    const jsonResponse = await response.json();
    if (response.status < 300) {
      return {
        statusCode: response.status,
        body: jsonResponse
      };
    } else {
      // TODO: ここで必要ならリダイレクト処理を実装する
      // if (response.status === 401 && !ignoreUnauthorizedHandling) {
      //   redirectToLogin(window.location.href);
      // }
      return {
        statusCode: response.status,
        error: {
          ...jsonResponse,
          statusCode: response.status,
          sasClientRequestId: (config.headers as RequestHeader)[
            'sas-client-request-id'
          ]
        } as ErrorResponse
      };
    }
  } catch (ex) {
    // BodyのJSONパースに失敗したケースなど予期せぬエラー
    // 主にインフラ回りの問題で発生する
    return {
      error: {
        statusCode: response.status,
        sasClientRequestId: (config.headers as RequestHeader)[
          'sas-client-request-id'
        ]
      } as ErrorResponse
    };
  }
};

/** エクスポネンシャルバックオフの待機時間(msec) */
const getWaitTimeExp = (retryCount: number) => Math.pow(2, retryCount) * 100;

/**
 * 指定ミリ秒待機する
 * @param time: 待機秒（msec）
 */
const sleep = (time: number) =>
  new Promise((resolve) => setTimeout(resolve, time));

// Abort発生時にエラーハンドリングが動いてしまい
// Firefoxのみ共通のエラー画面が表示されてしまうので
// キャンセルを見分けられるようにonbeforeunload時に明示的にAbortするようにする
const agent = window.navigator.userAgent.toLowerCase();
// Firefox以外では不要な処理なので、IE11（AbortController非対応）およびChromeは対象外とする
let ac = agent.indexOf('firefox') !== -1 ? new AbortController() : undefined;
ac &&
  (window.onbeforeunload = () => {
    if (ac) {
      ac.abort();
      ac = new AbortController();
    }
  });

/**
 * HTTPリクエストを行う
 * @param url URL
 * @param config リクエストの設定
 * @param ignoreUnauthorizedHandling 認証エラーのハンドリングをスキップするか否か
 * @param retryWithFetchError fetchでエラーが発生した際にリトライするか否か
 */
export const request = async <T extends Record<string, unknown> | undefined>(
  url: string,
  config: RequestInit,
  ignoreUnauthorizedHandling?: boolean,
  retryWithFetchError?: boolean
): Promise<ResponseInterface<T>> => {
  try {
    const fetchWithRetry = async (retryCount = 0) => {
      let response: Response;
      try {
        response = await fetch(url, {
          ...config,
          ...(ac && { signal: ac.signal })
        });

        if (
          retryCount < MAX_RETRIES &&
          !!(
            (config.method && config.method.toUpperCase() === 'GET') ||
            retryWithFetchError
          ) &&
          500 <= response.status &&
          response.status < 600
        ) {
          // 待機後に再試行
          await sleep(getWaitTimeExp(retryCount));
          response = await fetchWithRetry(++retryCount);
        }
      } catch (error) {
        if (
          retryCount < MAX_RETRIES &&
          !!(
            error.name === 'TypeError' &&
            ((config.method && config.method.toUpperCase() === 'GET') ||
              retryWithFetchError)
          )
        ) {
          // 待機後に再試行
          await sleep(getWaitTimeExp(retryCount));
          response = await fetchWithRetry(++retryCount);
        } else {
          throw error;
        }
      }
      return response;
    };

    const response = await fetchWithRetry();
    return createResponse(
      config,
      response,
      ignoreUnauthorizedHandling || false
    );
  } catch (error) {
    if (error.name === 'AbortError') {
      // リロードやURL遷移に伴う正常なキャンセルについてはStatusCode=0として扱う
      return {
        error: {
          statusCode: 0,
          sasClientRequestId: (config.headers as RequestHeader)[
            'sas-client-request-id'
          ]
        } as ErrorResponse
      };
    }
    // リクエストを送る前にエラーになった場合（基本的に発生しない）
    return {
      error: {
        statusCode: error,
        sasClientRequestId: (config.headers as RequestHeader)[
          'sas-client-request-id'
        ]
      } as ErrorResponse
    };
  }
};
